import React, { useState } from 'react';
import { Clock, Send } from 'lucide-react';
import styles from './assistant-interactive.module.css';

interface QuickTimeSuggestionsProps {
  content: string;
  onSelectTime: (timeStr: string) => void;
}

// Извлекает или генерирует подсказки по времени, если ассистент спрашивает о времени
export function parseTimeSuggestions(content: string): { needsTime: boolean; suggestions: string[] } {
  const lower = content.toLowerCase();
  
  // Проверяем, запрашивает ли ассистент время
  const timeQueryKeywords = [
    'выберите время',
    'укажите время',
    'какое время',
    'на какое время',
    'во сколько',
    'время занятия',
    'время записи',
    'начало и окончание'
  ];

  const needsTime = timeQueryKeywords.some((kw) => lower.includes(kw));

  if (!needsTime) {
    return { needsTime: false, suggestions: [] };
  }

  // Извлекаем конкретные предложенные времена из текста, если модель перечислила (например: 10:00, 11:30, 14:00)
  const timeRegex = /\b([01]?\d|2[0-3]):[0-5]\d\b/g;
  const matches = content.match(timeRegex);
  
  const uniqueTimes = Array.from(new Set(matches ?? []));

  // Если модель явно перечислила времена — используем их
  if (uniqueTimes.length > 0) {
    return { needsTime: true, suggestions: uniqueTimes.slice(0, 6) };
  }

  // Дефолтный сет удобных временных интервалов рабочего дня администратора
  const defaultTimes = ['09:00', '10:00', '11:00', '14:00', '18:00', '19:00', '20:00'];
  return { needsTime: true, suggestions: defaultTimes };
}

export const QuickTimeSuggestions: React.FC<QuickTimeSuggestionsProps> = ({ content, onSelectTime }) => {
  const { needsTime, suggestions } = parseTimeSuggestions(content);
  const [customTime, setCustomTime] = useState('10:00');

  if (!needsTime) {
    return null;
  }

  return (
    <div className={styles.quickReplySection} data-testid="assistant-quick-time-section">
      <div className={styles.quickReplyChips}>
        <span className="flex items-center gap-1 text-xs text-[hsl(var(--ui-muted-foreground))]">
          <Clock size={12} /> Выберите время:
        </span>
        {suggestions.map((t) => (
          <button
            key={t}
            type="button"
            className={styles.quickReplyChip}
            onClick={() => onSelectTime(t)}
            title={`Выбрать ${t}`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className={styles.inlineTimePickerBox}>
        <span>Или введите своё:</span>
        <input
          type="time"
          className={styles.inlineTimeInput}
          value={customTime}
          onChange={(e) => setCustomTime(e.target.value)}
          aria-label="Точное время записи"
        />
        <button
          type="button"
          className={styles.quickReplyChip}
          onClick={() => customTime && onSelectTime(customTime)}
          disabled={!customTime}
        >
          <Send size={12} /> Записать на {customTime}
        </button>
      </div>
    </div>
  );
};
